<template lang="pug">
.view-booking
  .left
    BookingSummaryPanel(
      :booking="booking"
      :mainBooker="mainBooker"
      :visitorsLength="visitorsLength"
      :amountPaid="amountPaid"
      :totalCostIncludingTax="totalCostIncludingTax"
      @checkin="checkin"
      @saveIntegrationFields="startSavingIntegrationFields"
      :integrationFieldDefinitionsList="integrationFieldDefinitionsList"
      )

  .main
    BriqPanel.schedule
      h2.h2.mb-S Scheduled products
      BookingScheduleTable(
        :plannedCheckinTime="booking.PlannedCheckinTime"
        :scheduledBookingProducts="scheduledBookingProducts"
        data-test-id="booking-schedule-table"
        )
    
    BriqPanel.schedule(v-if="nonScheduledBookingProducts.length > 0")
      h2.h2.mb-S Non-scheduled products
      BookingNonScheduleTable(
        :nonScheduledBookingProducts="nonScheduledBookingProducts"
        data-test-id="booking-non-schedule-table"
        )

    BriqPanel.costs
      .header
        h2.h2 Costs
        .actions
          BriqButton.mr-5(small v-if="businessAllowsVoucher" @click="showVoucherDialog = true" :disabled="disableAddVoucher" variant="secondary" data-test-id="booking-cost-add-voucher") Add voucher
          BriqButton(small v-if="businessAllowsDiscount" @click="showDiscountDialog = true" :disabled="disableAddDiscount" variant="secondary" data-test-id="booking-cost-add-discount") Add discount
      BookingCostsTable(
        :bookingCosts="bookingCosts"
        :displayPricesIncludingTax="displayPricesIncludingTax"
        @removeVoucher="promptRemoveVoucher"
        @removeDiscount="promptRemoveDiscount"
        @editDiscount="editDiscount"
        data-test-id="booking-costs-table"
        )

    BriqPanel(v-if="externalRemark" data-test-id="booking-notes-section")
      .header
        h2.h2 External Notes
      p.note-body {{ externalRemark.Remark }}

    BriqPanel(v-if="internalRemark" data-test-id="booking-notes-section")
      .header
        h2.h2 Internal Notes
      p.note-body {{ internalRemark.Remark }}

  BriqDialog(v-show="showVoucherDialog" @close="showVoucherDialog = false")
    h2.h2.mb-S Add voucher
    BriqInput.mb-S(label="Code" v-model="voucherCode" required :disabled="isPending")
    .actions
      BriqButton(variant="text" @click="showVoucherDialog = false" :disabled="isPending") Cancel
      BriqButton(@click="addVoucher" :disabled="isPending") {{ addVoucherText }}

  BriqDialog(v-show="showRemoveVoucherDialog" @close="showRemoveVoucherDialog = false")
    h2.h2.mb-S Remove voucher
    p.mb-S Are you sure you want to remove this voucher?
    BriqTextarea.mb-S(label="Reason" v-model="removeVoucherReason")
    .actions
      BriqButton(variant="text" @click="showRemoveVoucherDialog = false" :disabled="isPending") Cancel
      BriqButton(@click="removeVoucher" variant="red" :disabled="isPending") Remove

  BriqDialog(v-show="showDiscountDialog" @close="showDiscountDialog = false")
    h2.h2.mb-S {{ upsertDiscountLabel }}
    BriqRadio(v-model="discount.type" :options="discountTypeOptions" horizontal)
    BriqInput.mb-S(v-model="discount.amount" type="number" label="Amount" required :disabled="isPending" :error="discountError")
    .actions
      BriqButton(variant="text" @click="showDiscountDialog = false" :disabled="isPending") Cancel
      BriqButton(@click="saveDiscount" :disabled="isPending || discountError") Save

  BriqDialog(v-show="showRemoveDiscountDialog" @close="showRemoveDiscountDialog = false")
    h2.h2.mb-S Remove discount
    p.mb-S Are you sure you want to remove this discount?
    .actions
      BriqButton(variant="text" @click="showRemoveDiscountDialog = false" :disabled="isPending") Cancel
      BriqButton(@click="removeDiscount" variant="red" :disabled="isPending") Remove
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { reduce } from 'ramda'
import BookingSummaryPanel from './BookingSummaryPanel.vue'
import BookingCostsTable from './BookingCostsTable.vue'
import BookingScheduleTable from './BookingScheduleTable.vue'
import BookingNonScheduleTable from './BookingNonScheduleTable.vue'
import WarningLabel from '@v/components/WarningLabel.vue'
// import VoucherDialogs from './VoucherDialogs.vue' // todo: extract dialogs
import { AttendanceStatus, DiscountType } from '@v/bookings/details/BookingDetailsApiModel.js'
import BusinessFeature from '@v/models/BusinessFeatureEnum.js'
import Permission from '@v/models/UserPermissionEnum.js'

export default {
  name: 'BookingTab',
  components: {
    BookingSummaryPanel,
    BookingCostsTable,
    BookingScheduleTable,
    BookingNonScheduleTable,
    WarningLabel,
  },
  data() {
    return {
      showDiscountDialog: false,
      discount: { key: null, amount: 0, type: 'Price' },
      discountTypeOptions: { [DiscountType.Percent]: 'Percentage', [DiscountType.Price]: 'Amount' },
      showRemoveDiscountDialog: false,
      removeDiscountKey: null,
      editExistingDiscount: false,

      showVoucherDialog: false,
      showRemoveVoucherDialog: false,
      removeVoucherKey: null,
      removeVoucherReason: null,
      voucherCode: null,
      voucherIsVerified: false,
      lastCheckedVoucher: null,
    }
  },
  computed: {
    ...mapState('bookingDetails', ['bookingDetails', 'saveError']),
    ...mapGetters('bookingDetails', [
      'wasFetchedOk',
      'booking',
      'mainBooker',
      'bookingProducts',
      'scheduledBookingProducts',
      'nonScheduledBookingProducts',
      'bookingCosts',
      'displayPricesIncludingTax',
      'externalRemark',
      'internalRemark',
      'isPending',
      'visitorsLength',
      'totalCostIncludingTax',
      'amountPaid',
      'costsSubtotal',
      'integrationFieldDefinitionsList',
    ]),
    ...mapGetters('briqState', ['businessUnitHasFeature', 'userHasPermission']),
    upsertDiscountLabel() {
      return this.editExistingDiscount ? 'Edit discount' : 'Add discount'
    },
    participantsPerSegment() {
      return this.booking.QuantitiesPerSegment
    },
    totalParticipants() {
      return reduce((a, n) => a + n.Quantity, 0, this.participantsPerSegment)
    },
    addVoucherText() {
      return !this.voucherIsVerified ? 'Verify' : 'Add vouchercode'
    },
    discountError() {
      return this.discount.amount < 0 ? 'Discount may not be negative' : null
    },
    businessAllowsVoucher() {
      return this.businessUnitHasFeature(BusinessFeature.BookingAddVoucher)
    },
    businessAllowsDiscount() {
      return this.businessUnitHasFeature(BusinessFeature.BookingAddDiscount)
    },
    disableAddVoucher() {
      return this.pending || !this.userHasPermission(Permission.API_Admin_Booking_Discount_Create)
    },
    disableAddDiscount() {
      return (
        this.pending || !this.userHasPermission(Permission.API_Admin_Booking_Voucher_Redeem_Code)
      )
    },
  },
  methods: {
    ...mapActions('bookingDetails', ['saveAttendanceStatus', 'checkVoucher', 'saveVoucher']),

    // voucher methods
    async addVoucher() {
      if (!this.voucherIsVerified) {
        try {
          const voucher = await this.checkVoucher(this.voucherCode)
          this.lastCheckedVoucher = voucher
          if (!voucher.Valid) this.$notifyWarning('Voucher is not valid')
          else {
            this.voucherIsVerified = voucher.Valid
            this.$notifySuccess('Voucher is valid')
          }
        } catch (error) {
          if (error.response?.status === 404) {
            this.$notifyWarning('Voucher not found')
          } else
            this.$handleError({
              error,
              notification: 'Something wend wrong while checking voucher',
            })
        }
      } else {
        const success = await this.saveVoucher(this.voucherCode)
        if (success) {
          this.$notifySuccess('Voucher added')
          this.showVoucherDialog = false
        } else this.$handleError({ error: this.saveError, notification: 'Could not add voucher' })
        this.voucherCode = null
        this.lastCheckedVoucher = null
      }
    },
    promptRemoveVoucher(key) {
      this.removeVoucherKey = key
      this.showRemoveVoucherDialog = true
    },
    async removeVoucher() {
      const success = await this.$store.dispatch('bookingDetails/rollbackVoucher', {
        Key: this.removeVoucherKey,
        Reason: this.removeVoucherReason,
      })
      if (success) {
        this.$notifySuccess('Voucher removed')
        this.removeVoucherKey = null
        this.removeVoucherReason = null
        this.showRemoveVoucherDialog = false
      } else this.$handleError({ error: this.saveError, notification: 'Could not remove voucher' })
    },

    // discount methods
    async saveDiscount() {
      const saveModel = {
        Key: this.discount.key,
        Type: this.discount.type,
        Value: this.discount.amount,
      }
      const success = await this.$store.dispatch('bookingDetails/saveDiscount', saveModel)
      if (success) {
        this.$notifySuccess('Discount saved')
        this.discount.key = null
        this.discount.type = null
        this.discount.amount = 0
        this.editExistingDiscount = false
        this.showDiscountDialog = false
      } else this.$handleError({ error: this.saveError, notification: 'Could not save discount' })
    },
    promptRemoveDiscount(key) {
      this.removeDiscountKey = key
      this.showRemoveDiscountDialog = true
    },
    async removeDiscount() {
      const success = await this.$store.dispatch(
        'bookingDetails/removeDiscount',
        this.removeDiscountKey,
      )
      if (success) this.$notifySuccess('Discount removed')
      else this.$handleError({ error: this.saveError, notification: 'Could not remove discount' })
      this.removeDiscountKey = null
      this.showRemoveDiscountDialog = false
    },
    editDiscount(discount) {
      this.discount.amount = discount.Value
      this.discount.type = discount.Type
      this.discount.key = discount.Key
      this.editExistingDiscount = true
      this.showDiscountDialog = true
    },

    // other methods
    async checkin() {
      const success = await this.saveAttendanceStatus({ status: AttendanceStatus.CheckedIn })
      if (success) this.$notifySuccess('Booking checked in')
      else this.$notifyError('Could not check in booking')
    },

    async startSavingIntegrationFields(integrationFields) {
      const success = await this.$store.dispatch(
        'bookingDetails/saveIntegrationFields',
        integrationFields,
      )
      window.location.reload()
    },
  },
  beforeMount() {
    this.$setDocumentTitle(`Booking ${this.booking.Number} summary`)
  },
}
</script>

<style scoped lang="stylus">
.view-booking
  min-height 100vh
  background #eff3f7
  padding 30px
  display grid
  grid-template-columns 1fr 3fr
  gap 30px

.left, .main
  display flex
  flex-direction column

.costs
  .header
    display flex
    justify-content space-between
    margin-bottom 30px

.actions
  display flex
  justify-content center

.note-body
  white-space pre-line
</style>
